'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

export default function GalaSpotlightSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {if (e.isIntersecting) e.target.classList.add('active');});
      },
      { threshold: 0.1 }
    );
    sectionRef?.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-dark-bg noise-overlay w-full px-4 md:px-12 py-14 md:py-20 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14 reveal">
          <span className="inline-block px-4 py-1.5 rounded-full border border-accent/30 text-accent text-xs font-bold tracking-widest uppercase mb-4 md:mb-5">
            Historic First
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-6xl font-black text-white tracking-tight leading-tight mb-3 md:mb-4">
            The Gala Evening
          </h2>
          <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            For the very first time, HCAF&apos;s prestigious Gala Evening moves to Shanmukhananda Chandrasekhara Saraswati Auditorium - India&apos;s most celebrated concert venue.
          </p>
        </div>

        {/* Main Feature — asymmetric 60/40 */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-stretch">

          {/* Left: Artist + Venue */}
          <div className="lg:col-span-7 relative overflow-hidden rounded-3xl md:rounded-4xl min-h-[380px] md:min-h-[480px] reveal-left group">
            <AppImage
              src="/assets/images/javed-ali.png"
              alt="Javed Ali performing live on stage with dramatic red lighting"
              fill
              className="object-cover grayscale-hover"
              sizes="(max-width: 1024px) 100vw, 58vw" />
            
            {/* Strong scrim for white text */}
            <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/95 via-dark-bg/50 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-dark-bg/40 to-transparent" />

            <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-10">
              {/* Top badge */}
              <div className="flex justify-between items-start flex-wrap gap-2">
                <span className="px-3 md:px-4 py-1.5 rounded-full bg-primary/90 text-primary-foreground text-xs font-bold tracking-widest uppercase">
                  Exclusive to Members
                </span>
                <span className="px-3 md:px-4 py-1.5 rounded-full glass-dark text-accent text-xs font-bold">
                  Tuesday, 20 Oct 2026
                </span>
              </div>

              {/* Bottom content */}
              <div>
                <p className="text-accent text-xs font-bold tracking-widest uppercase mb-2 md:mb-3">Headline Artist</p>
                <h3 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tighter mb-2 md:mb-3 leading-none">
                  Javed Ali
                </h3>
                <p className="text-white/70 text-sm md:text-base mb-5 md:mb-6 max-w-md">
                  Kun Faaya Kun · Dil Ibaadat · Ek Din Teri Raahon Mein - live at Mumbai&apos;s most iconic stage.
                </p>
                <Link
                  href="/membership"
                  className="inline-flex items-center gap-3 bg-accent text-accent-foreground pl-6 md:pl-7 pr-3 py-3 rounded-full font-bold text-sm hover:bg-secondary hover:text-primary-foreground transition-all duration-300 group/btn shadow-gold">
                  
                  Secure Your Seat
                  <span className="bg-accent-foreground/10 p-2 rounded-full group-hover/btn:translate-x-0.5 transition-transform">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                    </svg>
                  </span>
                </Link>
              </div>
            </div>
          </div>

          {/* Right: Venue + Stats */}
          <div className="lg:col-span-5 flex flex-col gap-4 md:gap-5 reveal-right">
            {/* Venue Card */}
            <div className="bg-dark-card border border-accent/15 rounded-3xl md:rounded-4xl p-6 md:p-7 flex-1 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4 md:mb-5">
                  <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-accent/15 flex items-center justify-center text-accent flex-shrink-0">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm md:text-base">Shanmukhananda Chandrasekhara Saraswati Auditorium</h4>
                    <p className="text-white/50 text-xs">Sion East, Mumbai</p>
                  </div>
                </div>
                <p className="text-white/60 text-sm leading-relaxed mb-4 md:mb-5">
                  Inaugurated 1963 · India&apos;s largest and most iconic concert hall · Renowned for world-class acoustics and traditional Indian temple architecture.
                </p>
                <p className="text-white/50 text-xs">
                  Harbanslal Marg, Sion East · ~1 km from Kings Circle station
                </p>
              </div>
              <div className="mt-4 md:mt-5 pt-4 md:pt-5 border-t border-white/10 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-accent font-black text-xl md:text-2xl font-display">2,763</p>
                  <p className="text-white/50 text-xs">Seated capacity</p>
                </div>
                <div>
                  <p className="text-accent font-black text-xl md:text-2xl font-display">1963</p>
                  <p className="text-white/50 text-xs">Year inaugurated</p>
                </div>
              </div>
            </div>

            {/* Past Artists Card */}
            <div className="bg-dark-card border border-accent/15 rounded-3xl md:rounded-4xl p-6 md:p-7">
              <p className="text-white/50 text-xs font-bold tracking-widest uppercase mb-3 md:mb-4">Past Gala Artists</p>
              <div className="flex flex-col gap-3">
                {[
                { name: 'Monali Thakur', desc: 'Bollywood Playback Singer' },
                { name: 'Chandrabindoo', desc: 'Iconic Bengali Band, Kolkata' }]?.
                map((artist) =>
                <div key={artist?.name} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-accent flex-shrink-0" />
                    <div>
                      <p className="text-white font-semibold text-sm">{artist?.name}</p>
                      <p className="text-white/40 text-xs">{artist?.desc}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-accent text-xs font-bold tracking-wide">2026 Upgrade ↑ Shanmukhananda Chandrasekhara Saraswati Auditorium</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}